/**
 * ============================================================================
 * USER ROLES DEFINITION
 * ============================================================================
 *
 * All user roles in the system.
 * These must match EXACTLY with the roles in your backend/database.
 *
 * NOTE: Route permissions are now in routes.ts
 * ============================================================================
 */

export enum UserRole {
  ADMIN = 'admin',
  TRAINEE = 'trainee',
  TRAINER = 'trainer',
  SUPER_ADMIN = 'super_admin',
}

/**
 * Display names for roles (for UI)
 */
export const ROLE_DISPLAY_NAMES: Record<UserRole, string> = {
  [UserRole.ADMIN]: 'Administrator',
  [UserRole.TRAINEE]: 'Learner',
  [UserRole.TRAINER]: 'Trainer',
  [UserRole.SUPER_ADMIN]: 'Super Admin',
};

/**
 * Get the default dashboard route for a user role
 */
export const getDefaultRouteForRole = (userRole: UserRole | undefined): string => {
  switch (userRole) {
    case UserRole.TRAINEE:
      return '/trainee/home';
    case UserRole.ADMIN:
      return '/admin/dashboard';
    case UserRole.TRAINER:
      return '/trainer/dashboard';
    case UserRole.SUPER_ADMIN:
      return '/superadmin/dashboard';
    default:
      return '/login';
  }
};

/**
 * Legacy compatibility - kept for backward compatibility
 * @deprecated Use getDefaultRouteForRole instead
 */
export const getDefaultPathForRole = (role: UserRole): string => {
  return getDefaultRouteForRole(role);
};

/**
 * Check if a role is an admin role
 */
export const isAdminRole = (userRole: UserRole | undefined): boolean => {
  return userRole === UserRole.ADMIN;
};

/**
 * Check if a role is a trainee role
 */
export const isTraineeRole = (userRole: UserRole | undefined): boolean => {
  return userRole === UserRole.TRAINEE;
};

/**
 * Check if a role is a trainer role
 */
export const isTrainerRole = (userRole: UserRole | undefined): boolean => {
  return userRole === UserRole.TRAINER;
};

/**
 * Check if a role is a super admin role
 */
export const isSuperAdminRole = (userRole: UserRole | undefined): boolean => {
  return userRole === UserRole.SUPER_ADMIN;
};

/**
 * Get role from string (with type safety)
 */
export const getRoleFromString = (role: string | undefined): UserRole | undefined => {
  if (!role) return undefined;
  const normalizedRole = role.toLowerCase();
  if (normalizedRole === 'admin') return UserRole.ADMIN;
  if (normalizedRole === 'trainee') return UserRole.TRAINEE;
  if (normalizedRole === 'trainer') return UserRole.TRAINER;
  if (normalizedRole === 'super_admin' || normalizedRole === 'superadmin') return UserRole.SUPER_ADMIN;
  return undefined;
};

/**
 * Legacy Route Permissions (for backward compatibility)
 * @deprecated Use routes.ts configuration instead
 */
export const ROUTE_PERMISSIONS: Record<string, UserRole[]> = {
  '/workspace': [UserRole.ADMIN, UserRole.TRAINEE, UserRole.TRAINER, UserRole.SUPER_ADMIN],
};

/**
 * Legacy permission check (for backward compatibility)
 * @deprecated Import hasRoutePermission from routes.ts instead
 */
export const hasRoutePermission = (userRole: UserRole, path: string): boolean => {
  // Check each permission entry
  for (const [routePrefix, allowedRoles] of Object.entries(ROUTE_PERMISSIONS)) {
    if (path.startsWith(routePrefix)) {
      return allowedRoles.includes(userRole);
    }
  }
  // If no specific permission is defined, allow access
  return true;
};
